import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Crown, Image, FileText, User, LogOut, Calendar, MapPin, Camera, ChevronRight, Edit2, Check, Bell, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import AiToolkit from "@/components/AiToolkit";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.4 } }),
};

export default function ClientDashboard() {
  const [user, setUser] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState("galleries");
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: "", phone: "" });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [profileRes, galleriesRes, invoicesRes, notifsRes, unreadRes] = await Promise.all([
          axios.get(`${API}/client/profile`, { withCredentials: true }),
          axios.get(`${API}/client/galleries`, { withCredentials: true }),
          axios.get(`${API}/client/invoices`, { withCredentials: true }),
          axios.get(`${API}/client/notifications`, { withCredentials: true }),
          axios.get(`${API}/client/notifications/unread-count`, { withCredentials: true }),
        ]);
        setUser(profileRes.data);
        setProfileForm({ name: profileRes.data.name || "", phone: profileRes.data.phone || "" });
        setGalleries(galleriesRes.data);
        setInvoices(invoicesRes.data);
        setNotifications(notifsRes.data);
        setUnreadCount(unreadRes.data.count);
      } catch {
        toast.error("Session expired. Please sign in again.");
        navigate("/vip");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [navigate]);

  // Real-time WebSocket notifications
  useEffect(() => {
    if (!user?.email) return;
    const wsUrl = `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws")}/ws/notifications/${user.email}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      ws.onmessage = (evt) => {
        const data = JSON.parse(evt.data);
        if (data.type === "notification") {
          setNotifications(prev => [{ ...data.data, read: false, created_at: new Date().toISOString() }, ...prev]);
          setUnreadCount(c => c + 1);
          toast.info(data.data.title, { description: data.data.message });
        }
      };
    } catch {}
    return () => { ws?.close(); };
  }, [user?.email]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* silent */ }
    navigate("/vip");
  };

  const handleProfileSave = async () => {
    try {
      await axios.put(`${API}/client/profile`, profileForm, { withCredentials: true });
      setUser((prev) => ({ ...prev, ...profileForm }));
      setEditingProfile(false);
      toast.success("Profile updated successfully.");
    } catch {
      toast.error("Failed to update profile.");
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/client/notifications/read-all`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  const markOneRead = async (id) => {
    try {
      await axios.patch(`${API}/client/notifications/${id}/read`, {}, { withCredentials: true });
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center">
        <div className="text-gold font-body animate-pulse">Loading your portal...</div>
      </div>
    );
  }

  const TABS = [
    { id: "galleries", label: "My Galleries", icon: Image },
    { id: "ai-toolkit", label: "AI Toolkit", icon: Sparkles },
    { id: "notifications", label: "Notifications", icon: Bell, badge: unreadCount },
    { id: "invoices", label: "Invoices", icon: FileText },
    { id: "profile", label: "Profile", icon: User },
  ];

  return (
    <div data-testid="client-dashboard" className="pt-20 min-h-screen bg-luxury-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-gold/15 flex items-center justify-center">
              <Crown size={22} className="text-gold" />
            </div>
            <div>
              <p className="text-xs font-body uppercase tracking-[0.2em] text-gold mb-0.5">VIP Client Portal</p>
              <h1 className="font-heading text-2xl text-white">Welcome, {user?.name || "Client"}</h1>
            </div>
          </div>
          <button data-testid="client-logout" onClick={handleLogout} className="flex items-center gap-2 text-white/50 hover:text-white text-sm font-body transition-colors">
            <LogOut size={16} /> Sign Out
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              data-testid={`client-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-body whitespace-nowrap transition-all ${
                activeTab === tab.id ? "bg-gold/15 text-gold border border-gold/30" : "border border-white/10 text-white/50 hover:text-white"
              }`}
            >
              <tab.icon size={16} /> {tab.label}
              {tab.badge > 0 && (
                <span className="bg-gold text-black text-[10px] font-medium w-5 h-5 rounded-full flex items-center justify-center">{tab.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* Galleries Tab */}
        {activeTab === "galleries" && (
          <motion.div initial="hidden" animate="visible">
            {galleries.length === 0 ? (
              <motion.div variants={fadeUp} custom={0} className="glass-card p-12 text-center">
                <Image size={48} className="text-gold/30 mx-auto mb-4" />
                <h3 className="text-white font-heading text-xl mb-2">No Galleries Yet</h3>
                <p className="text-white/40 font-body text-sm">Your event galleries will appear here once our team publishes them after your event.</p>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {galleries.map((gallery, i) => (
                  <motion.div key={gallery.id} variants={fadeUp} custom={i} data-testid={`gallery-card-${gallery.id}`}>
                    <Link to={`/vip/gallery/${gallery.id}`} className="glass-card block group hover:border-gold/30 transition-all overflow-hidden">
                      {/* Cover Image */}
                      {gallery.cover_url ? (
                        <div className="h-40 overflow-hidden">
                          <img src={gallery.cover_url} alt={gallery.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                      ) : (
                        <div className="h-40 bg-gradient-to-br from-gold/10 to-gold/5 flex items-center justify-center">
                          <Camera size={32} className="text-gold/30" />
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="text-white font-body font-medium text-sm group-hover:text-gold transition-colors">{gallery.title}</h3>
                            <p className="text-white/40 text-xs font-body">{gallery.event_type}</p>
                          </div>
                          <ChevronRight size={18} className="text-white/20 group-hover:text-gold transition-colors" />
                        </div>
                        {gallery.description && (
                          <p className="text-white/50 font-body font-light text-xs leading-relaxed mb-3">{gallery.description}</p>
                        )}
                        <div className="flex items-center gap-4 text-white/30 text-xs font-body">
                          <span className="flex items-center gap-1"><Calendar size={12} /> {gallery.event_date}</span>
                          <span className="flex items-center gap-1"><Image size={12} /> {gallery.media_count} items</span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* AI Toolkit Tab */}
        {activeTab === "ai-toolkit" && <AiToolkit />}

        {/* Notifications Tab */}
        {activeTab === "notifications" && (
          <motion.div initial="hidden" animate="visible">
            {unreadCount > 0 && (
              <div className="flex justify-end mb-4">
                <button data-testid="mark-all-read-btn" onClick={markAllRead} className="text-gold text-xs font-body hover:text-gold-light transition-colors">
                  Mark all as read
                </button>
              </div>
            )}
            {notifications.length === 0 ? (
              <motion.div variants={fadeUp} custom={0} className="glass-card p-12 text-center">
                <Bell size={48} className="text-gold/30 mx-auto mb-4" />
                <h3 className="text-white font-heading text-xl mb-2">No Notifications</h3>
                <p className="text-white/40 font-body text-sm">You'll be notified when new media is added to your galleries.</p>
              </motion.div>
            ) : (
              <div className="space-y-3">
                {notifications.map((notif, i) => (
                  <motion.div
                    key={notif.id}
                    variants={fadeUp}
                    custom={i}
                    data-testid={`notification-${notif.id}`}
                    className={`glass-card p-5 cursor-pointer transition-all ${!notif.read ? "border-l-2 border-l-gold" : "opacity-70"}`}
                    onClick={() => {
                      if (!notif.read) markOneRead(notif.id);
                      if (notif.link) navigate(notif.link);
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {!notif.read && <span className="w-2 h-2 bg-gold rounded-full flex-shrink-0" />}
                          <h4 className="text-white font-body text-sm font-medium">{notif.title}</h4>
                        </div>
                        <p className="text-white/50 font-body text-xs leading-relaxed">{notif.message}</p>
                      </div>
                      <span className="text-white/20 text-[10px] font-body whitespace-nowrap">
                        {notif.created_at ? new Date(notif.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : ""}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Invoices Tab */}
        {activeTab === "invoices" && (
          <motion.div initial="hidden" animate="visible">
            {invoices.length === 0 ? (
              <motion.div variants={fadeUp} custom={0} className="glass-card p-12 text-center">
                <FileText size={48} className="text-gold/30 mx-auto mb-4" />
                <h3 className="text-white font-heading text-xl mb-2">No Invoices</h3>
                <p className="text-white/40 font-body text-sm">Your payment history and invoices will appear here.</p>
              </motion.div>
            ) : (
              <div className="space-y-4">
                {invoices.map((inv, i) => (
                  <motion.div key={inv.id || inv.session_id || i} variants={fadeUp} custom={i} className="glass-card p-6" data-testid={`invoice-${i}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <h3 className="text-white font-body text-sm font-medium">{inv.package_name || "Payment"}</h3>
                        <p className="text-white/40 text-xs font-body mt-1">
                          {inv.created_at ? new Date(inv.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "N/A"}
                        </p>
                        {inv.session_id && (
                          <p className="text-white/20 text-xs font-body mt-1">Ref: {inv.session_id.slice(0, 24)}...</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-gold font-body font-medium text-lg">${inv.amount?.toLocaleString()}</p>
                        <span className={`inline-block mt-1 px-3 py-0.5 text-xs font-body ${
                          inv.payment_status === "paid" ? "bg-green-500/15 text-green-400" :
                          inv.payment_status === "initiated" ? "bg-gold/15 text-gold" : "bg-red-500/15 text-red-400"
                        }`}>
                          {inv.payment_status === "paid" ? "Paid" : inv.payment_status === "initiated" ? "Pending" : inv.payment_status}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Profile Tab */}
        {activeTab === "profile" && (
          <motion.div initial="hidden" animate="visible">
            <motion.div variants={fadeUp} custom={0} className="glass-card p-8 max-w-xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-white font-heading text-xl">Your Profile</h3>
                {!editingProfile ? (
                  <button
                    data-testid="edit-profile-btn"
                    onClick={() => setEditingProfile(true)}
                    className="flex items-center gap-1 text-gold text-xs font-body hover:text-gold-light transition-colors"
                  >
                    <Edit2 size={14} /> Edit
                  </button>
                ) : (
                  <button
                    data-testid="save-profile-btn"
                    onClick={handleProfileSave}
                    className="flex items-center gap-1 text-gold text-xs font-body hover:text-gold-light transition-colors"
                  >
                    <Check size={14} /> Save
                  </button>
                )}
              </div>

              <div className="space-y-5">
                <div>
                  <label className="text-white/40 text-xs font-body uppercase tracking-wider mb-2 block">Name</label>
                  {editingProfile ? (
                    <Input
                      data-testid="profile-name-input"
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      className="bg-white/5 border-white/10 text-white h-10 rounded-sm"
                    />
                  ) : (
                    <p className="text-white font-body text-sm" data-testid="profile-name">{user?.name || "—"}</p>
                  )}
                </div>
                <div>
                  <label className="text-white/40 text-xs font-body uppercase tracking-wider mb-2 block">Email</label>
                  <p className="text-white/70 font-body text-sm" data-testid="profile-email">{user?.email}</p>
                </div>
                <div>
                  <label className="text-white/40 text-xs font-body uppercase tracking-wider mb-2 block">Phone</label>
                  {editingProfile ? (
                    <Input
                      data-testid="profile-phone-input"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      placeholder="+1 (876) 000-0000"
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-10 rounded-sm"
                    />
                  ) : (
                    <p className="text-white font-body text-sm" data-testid="profile-phone">{user?.phone || "—"}</p>
                  )}
                </div>
                <div>
                  <label className="text-white/40 text-xs font-body uppercase tracking-wider mb-2 block">Membership</label>
                  <div className="flex items-center gap-2">
                    <Crown size={14} className="text-gold" />
                    <span className="text-gold font-body text-sm font-medium">VIP Client</span>
                  </div>
                </div>
                <div>
                  <label className="text-white/40 text-xs font-body uppercase tracking-wider mb-2 block">Member Since</label>
                  <p className="text-white/60 font-body text-sm">
                    {user?.created_at ? new Date(user.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "—"}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
